import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FileText, Send, List, User, Settings, Users, CheckCircle, XCircle, Clock, Download, Plus, Trash2, RefreshCw, LogOut, ChevronDown, ChevronUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const LOGO_URL = "https://customer-assets-eiarnc6j.emergentagent.net/job_equestrian-hub-20/artifacts/sz2yv83s_Fekete%2520hatter%2C%2520feher.webp";

export default function Hianyzas() {
  // Auth state
  const [userToken, setUserToken] = useState(localStorage.getItem('hianyzas_user_token') || '');
  const [adminToken, setAdminToken] = useState(localStorage.getItem('hianyzas_admin_token') || '');
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  
  // Login state
  const [loginCode, setLoginCode] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  
  // User menu state
  const [activeTab, setActiveTab] = useState('new');
  
  // Form state
  const [formType, setFormType] = useState('Igazolás');
  const [formClass, setFormClass] = useState('');
  const [formDate, setFormDate] = useState('');
  const [formReason, setFormReason] = useState('');
  const [reasons, setReasons] = useState([]);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  // Requests state
  const [myRequests, setMyRequests] = useState([]);
  const [expandedRequest, setExpandedRequest] = useState(null);
  
  // Admin state
  const [adminTab, setAdminTab] = useState('pending');
  const [allUsers, setAllUsers] = useState([]);
  const [allReasons, setAllReasons] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [allRequests, setAllRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  
  // New user form
  const [newUserName, setNewUserName] = useState('');
  const [newUserClass, setNewUserClass] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newReasonName, setNewReasonName] = useState('');
  
  const [editingUser, setEditingUser] = useState(null);

  // Fetch reasons
  const fetchReasons = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/hianyzas/reasons`);
      setReasons(res.data);
    } catch (err) {
      console.error('Failed to fetch reasons:', err);
    }
  }, []);

  // Fetch current user
  const fetchCurrentUser = useCallback(async () => {
    if (!userToken) return;
    try {
      const res = await axios.get(`${API}/hianyzas/me`, { params: { token: userToken } });
      setCurrentUser(res.data);
      setFormClass(res.data.class_name || '');
    } catch (err) {
      localStorage.removeItem('hianyzas_user_token');
      setUserToken('');
      setCurrentUser(null);
    }
  }, [userToken]);

  // Fetch my requests
  const fetchMyRequests = useCallback(async () => {
    if (!userToken) return;
    try {
      const res = await axios.get(`${API}/hianyzas/requests`, { params: { token: userToken } });
      setMyRequests(res.data);
    } catch (err) {
      console.error('Failed to fetch requests:', err);
    }
  }, [userToken]);

  // Admin: Fetch all users
  const fetchAllUsers = useCallback(async () => {
    if (!adminToken) return;
    try {
      const res = await axios.get(`${API}/hianyzas/admin/users`, { params: { token: adminToken } });
      setAllUsers(res.data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  }, [adminToken]);

  // Admin: Fetch all reasons
  const fetchAllReasons = useCallback(async () => {
    if (!adminToken) return;
    try {
      const res = await axios.get(`${API}/hianyzas/admin/reasons`, { params: { token: adminToken } });
      setAllReasons(res.data);
    } catch (err) {
      console.error('Failed to fetch reasons:', err);
    }
  }, [adminToken]);

  // Admin: Fetch pending requests
  const fetchPendingRequests = useCallback(async () => {
    if (!adminToken) return;
    try {
      const res = await axios.get(`${API}/hianyzas/admin/requests/pending`, { params: { token: adminToken } });
      setPendingRequests(res.data);
    } catch (err) {
      console.error('Failed to fetch pending requests:', err);
    }
  }, [adminToken]);

  // Admin: Fetch all requests
  const fetchAllRequests = useCallback(async () => {
    if (!adminToken) return;
    try {
      const res = await axios.get(`${API}/hianyzas/admin/requests`, { params: { token: adminToken } });
      setAllRequests(res.data);
    } catch (err) {
      console.error('Failed to fetch all requests:', err);
    }
  }, [adminToken]);

  useEffect(() => {
    fetchReasons();
  }, [fetchReasons]);

  useEffect(() => {
    if (userToken) {
      fetchCurrentUser();
      fetchMyRequests();
    }
  }, [userToken, fetchCurrentUser, fetchMyRequests]);

  useEffect(() => {
    if (adminToken) {
      setIsAdmin(true);
      fetchAllUsers();
      fetchAllReasons();
      fetchPendingRequests();
      fetchAllRequests();
    }
  }, [adminToken, fetchAllUsers, fetchAllReasons, fetchPendingRequests, fetchAllRequests]);

  // User login
  const handleUserLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    if (loginCode.length !== 8 || !/^[1-9]+$/.test(loginCode)) {
      setLoginError('A kód 8 számjegyből állhat (1-9), 0 nem lehet benne');
      return;
    }
    
    try {
      const res = await axios.post(`${API}/hianyzas/login`, { code: loginCode });
      localStorage.setItem('hianyzas_user_token', res.data.token);
      setUserToken(res.data.token);
      setCurrentUser(res.data.user);
      setFormClass(res.data.user.class_name || '');
      setLoginCode('');
    } catch (err) {
      setLoginError(err.response?.data?.detail || 'Hibás kód');
    }
  };

  // Admin login
  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      const res = await axios.post(`${API}/hianyzas/admin/login`, { password: adminPassword });
      localStorage.setItem('hianyzas_admin_token', res.data.token);
      setAdminToken(res.data.token);
      setIsAdmin(true);
      setAdminPassword('');
      setShowAdminLogin(false);
    } catch (err) {
      setLoginError(err.response?.data?.detail || 'Hibás jelszó');
    }
  };

  // Logout
  const handleLogout = () => {
    localStorage.removeItem('hianyzas_user_token');
    localStorage.removeItem('hianyzas_admin_token');
    setUserToken('');
    setAdminToken('');
    setCurrentUser(null);
    setIsAdmin(false);
  };

  // Submit request
  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    setSubmitSuccess(false);
    
    try {
      await axios.post(`${API}/hianyzas/requests`, {
        type: formType,
        class_name: formClass,
        date: formDate,
        reason: formReason
      }, { params: { token: userToken } });
      
      setSubmitSuccess(true);
      setFormDate('');
      setFormReason('');
      fetchMyRequests();
      
      setTimeout(() => setSubmitSuccess(false), 3000);
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  // Admin: Create user
  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/hianyzas/admin/users`, {
        name: newUserName,
        class_name: newUserClass,
        email: newUserEmail
      }, { params: { token: adminToken } });
      
      setNewUserName('');
      setNewUserClass('');
      setNewUserEmail('');
      fetchAllUsers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  // Admin: Update user
  const handleUpdateUser = async (userId, data) => {
    try {
      await axios.put(`${API}/hianyzas/admin/users/${userId}`, data, { params: { token: adminToken } });
      fetchAllUsers();
      setEditingUser(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  // Admin: Regenerate code
  const handleRegenerateCode = async (userId) => {
    try {
      const res = await axios.post(`${API}/hianyzas/admin/users/${userId}/regenerate-code`, {}, { params: { token: adminToken } });
      alert(`Új kód: ${res.data.code}`);
      fetchAllUsers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  // Admin: Add reason
  const handleAddReason = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/hianyzas/admin/reasons`, { name: newReasonName }, { params: { token: adminToken } });
      setNewReasonName('');
      fetchAllReasons();
      fetchReasons();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  // Admin: Delete reason
  const handleDeleteReason = async (reasonId) => {
    if (!window.confirm('Biztosan törölni szeretnéd?')) return;
    try {
      await axios.delete(`${API}/hianyzas/admin/reasons/${reasonId}`, { params: { token: adminToken } });
      fetchAllReasons();
      fetchReasons();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  // Admin: Upload PDF
  const handleUploadPdf = async (requestId, file) => {
    setUploadingPdf(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/hianyzas/admin/requests/${requestId}/upload-pdf?token=${adminToken}`, formData);
      fetchPendingRequests();
      if (selectedRequest?.id === requestId) {
        setSelectedRequest({ ...selectedRequest, pdf_path: 'uploaded' });
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba a feltöltésnél');
    }
    setUploadingPdf(false);
  };

  // Admin: Make decision
  const handleDecision = async (requestId, decision) => {
    try {
      await axios.post(`${API}/hianyzas/admin/requests/${requestId}/decision`, { decision }, { params: { token: adminToken } });
      fetchPendingRequests();
      fetchAllRequests();
      setSelectedRequest(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  // Get date constraints
  const getDateConstraints = () => {
    const today = new Date().toISOString().split('T')[0];
    const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];
    
    if (formType === 'Igazolás' || formType === 'E-igazolás') {
      return { max: today };
    } else {
      return { min: tomorrow };
    }
  };

  // Status helpers
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return { bg: 'bg-yellow-50', border: 'border-yellow-300', text: 'text-yellow-700', label: 'Feldolgozásra vár' };
      case 'approved': return { bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-700', label: 'Elfogadva' };
      case 'rejected': return { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-700', label: 'Elutasítva' };
      default: return { bg: 'bg-gray-50', border: 'border-gray-300', text: 'text-gray-700', label: status };
    }
  };

  const formatDate = (isoDate) => {
    if (!isoDate) return '';
    const d = new Date(isoDate);
    return d.toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // LOGIN SCREEN
  if (!userToken && !isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-emerald-950">
        {/* Modern header with glassmorphism */}
        <div className="backdrop-blur-sm bg-white/10 border-b border-white/20 py-4 px-6">
          <div className="flex items-center justify-center gap-4">
            <img src={LOGO_URL} alt="Gepárd Lovas Egyesület" className="w-16 h-16 rounded-2xl shadow-lg" />
            <div className="text-center">
              <h1 className="text-2xl font-bold text-white tracking-wide">Gepárd Lovas Egyesület</h1>
              <p className="text-emerald-200 text-sm font-light">Orosháza • 2026</p>
            </div>
          </div>
        </div>

        <div className="max-w-md mx-auto mt-16 px-4">
          {!showAdminLogin ? (
            <div className="backdrop-blur-xl bg-white/95 rounded-3xl shadow-2xl border border-white/50 p-8 transform hover:scale-[1.01] transition-transform">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-2xl mb-4 shadow-lg">
                  <FileText size={32} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800">Igazoláskezelő</h2>
                <p className="text-gray-500 mt-1">Kérj igazolást vagy kikérőt online</p>
              </div>
              
              <form onSubmit={handleUserLogin} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Belépési kód</label>
                  <input
                    type="text"
                    maxLength={8}
                    value={loginCode}
                    onChange={(e) => setLoginCode(e.target.value.replace(/[^1-9]/g, ''))}
                    className="w-full px-5 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-emerald-500/30 focus:border-emerald-500 text-center text-3xl tracking-[0.5em] font-mono text-gray-900 bg-gray-50 transition-all"
                    placeholder="••••••••"
                  />
                  <p className="text-xs text-gray-400 mt-2 text-center">8 számjegy (1-9)</p>
                </div>
                
                {loginError && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-600 text-sm text-center">
                    {loginError}
                  </div>
                )}
                
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white font-bold py-4 rounded-2xl transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  Belépés
                </button>
              </form>
              
              <div className="mt-8 pt-6 border-t border-gray-100 text-center">
                <button
                  onClick={() => { setShowAdminLogin(true); setLoginError(''); }}
                  className="text-emerald-600 hover:text-emerald-700 text-sm font-semibold flex items-center justify-center gap-2 mx-auto"
                >
                  <Settings size={16} /> Admin belépés
                </button>
              </div>
            </div>
          ) : (
            <div className="backdrop-blur-xl bg-white/95 rounded-3xl shadow-2xl border border-white/50 p-8">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-slate-600 to-slate-800 rounded-2xl mb-4 shadow-lg">
                  <Settings size={32} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800">Admin belépés</h2>
              </div>
              
              <form onSubmit={handleAdminLogin} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Jelszó</label>
                  <input
                    type="password"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    className="w-full px-5 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-emerald-500/30 focus:border-emerald-500 text-gray-900 bg-gray-50 transition-all"
                  />
                </div>
                
                {loginError && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-600 text-sm text-center">
                    {loginError}
                  </div>
                )}
                
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-800 hover:to-slate-900 text-white font-bold py-4 rounded-2xl transition-all shadow-lg hover:shadow-xl"
                >
                  Belépés
                </button>
              </form>
              
              <div className="mt-8 pt-6 border-t border-gray-100 text-center">
                <button
                  onClick={() => { setShowAdminLogin(false); setLoginError(''); }}
                  className="text-gray-500 hover:text-gray-700 text-sm font-medium"
                >
                  ← Vissza
                </button>
              </div>
            </div>
          )}
        </div>
        
        <p className="text-center text-emerald-300/50 text-xs mt-8 pb-4">© 2026 Gepárd Lovas Egyesület</p>
      </div>
    );
  }

  // ADMIN INTERFACE
  if (isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        {/* Modern admin header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white py-4 px-6 shadow-xl">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Logo" className="w-10 h-10 rounded-xl" />
              <div>
                <h1 className="text-lg font-bold">Admin Felület</h1>
                <p className="text-slate-400 text-xs">Igazoláskezelő • 2026</p>
              </div>
            </div>
            <button onClick={handleLogout} className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-xl transition-colors">
              <LogOut size={18} /> Kilépés
            </button>
          </div>
        </div>

        {/* Admin tabs */}
        <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-10">
          <div className="max-w-6xl mx-auto flex gap-1 px-4 py-3 overflow-x-auto">
            {[
              { id: 'pending', label: 'Jóváhagyandó', icon: Clock, count: pendingRequests.length },
              { id: 'users', label: 'Felhasználók', icon: Users },
              { id: 'reasons', label: 'Indokok', icon: Settings },
              { id: 'all', label: 'Összes kérelem', icon: List },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setAdminTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold whitespace-nowrap transition-all ${
                  adminTab === tab.id 
                    ? 'bg-gradient-to-r from-emerald-600 to-emerald-700 text-white shadow-lg shadow-emerald-500/30' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <tab.icon size={18} />
                {tab.label}
                {tab.count > 0 && (
                  <span className="bg-amber-400 text-amber-900 text-xs font-bold px-2.5 py-0.5 rounded-full animate-pulse">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 py-6">
          {/* Pending requests */}
          {adminTab === 'pending' && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-gray-800">Jóváhagyandó kérelmek</h2>
              
              {pendingRequests.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Nincs feldolgozásra váró kérelem</p>
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  {pendingRequests.map(req => (
                    <div
                      key={req.id}
                      onClick={() => setSelectedRequest(req)}
                      className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 cursor-pointer hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="bg-yellow-200 text-yellow-800 text-xs font-semibold px-2 py-1 rounded-full">
                          {req.type}
                        </span>
                        <span className="text-sm text-gray-500">{formatDate(req.created_at)}</span>
                      </div>
                      <p className="font-semibold text-gray-800">{req.user_name}</p>
                      <p className="text-sm text-gray-600">{req.class_name} • {req.date}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Selected request modal */}
              {selectedRequest && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                  <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-800 mb-4">Kérelem részletei</h3>
                      
                      <div className="space-y-3 mb-6">
                        <p><span className="text-gray-500">Típus:</span> <span className="font-medium">{selectedRequest.type}</span></p>
                        <p><span className="text-gray-500">Név:</span> <span className="font-medium">{selectedRequest.user_name}</span></p>
                        <p><span className="text-gray-500">Osztály / Tagozat:</span> <span className="font-medium">{selectedRequest.class_name}</span></p>
                        <p><span className="text-gray-500">Dátum:</span> <span className="font-medium">{selectedRequest.date}</span></p>
                        <p><span className="text-gray-500">Indok:</span> <span className="font-medium">{selectedRequest.reason}</span></p>
                        <p><span className="text-gray-500">Beküldve:</span> <span className="font-medium">{formatDate(selectedRequest.created_at)}</span></p>
                        <p><span className="text-gray-500">Email:</span> <span className="font-medium">{selectedRequest.user_email || 'Nincs'}</span></p>
                      </div>

                      {/* PDF upload for Igazolás and Kikérő */}
                      {(selectedRequest.type === 'Igazolás' || selectedRequest.type === 'Kikérő') && !selectedRequest.pdf_path && (
                        <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
                          <p className="text-sm text-gray-600 mb-3">PDF feltöltése kötelező a döntés előtt</p>
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={(e) => e.target.files[0] && handleUploadPdf(selectedRequest.id, e.target.files[0])}
                            className="w-full text-sm"
                            disabled={uploadingPdf}
                          />
                        </div>
                      )}

                      {/* Buttons */}
                      {((selectedRequest.type === 'E-igazolás' || selectedRequest.type === 'E-kikérő') || selectedRequest.pdf_path) ? (
                        <div className="flex gap-3">
                          <button
                            onClick={() => handleDecision(selectedRequest.id, 'approve')}
                            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2"
                          >
                            <CheckCircle size={20} /> Elfogadás
                          </button>
                          <button
                            onClick={() => handleDecision(selectedRequest.id, 'reject')}
                            className="flex-1 bg-red-500 hover:bg-red-600 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2"
                          >
                            <XCircle size={20} /> Elutasítás
                          </button>
                        </div>
                      ) : (
                        <p className="text-center text-gray-500 text-sm">Töltsd fel a PDF-et a döntéshez</p>
                      )}

                      <button
                        onClick={() => setSelectedRequest(null)}
                        className="w-full mt-4 py-2 text-gray-500 hover:text-gray-700"
                      >
                        Bezárás
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Users management */}
          {adminTab === 'users' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-gray-800">Felhasználók kezelése</h2>
              
              {/* Add user form */}
              <form onSubmit={handleCreateUser} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <h3 className="font-semibold text-gray-700 mb-3">Új felhasználó</h3>
                <div className="grid gap-3 md:grid-cols-4">
                  <input
                    type="text"
                    value={newUserName}
                    onChange={(e) => setNewUserName(e.target.value)}
                    placeholder="Név *"
                    className="px-3 py-2 border border-gray-200 rounded-lg text-gray-900"
                    required
                  />
                  <input
                    type="text"
                    value={newUserClass}
                    onChange={(e) => setNewUserClass(e.target.value)}
                    placeholder="Osztály / Tagozat"
                    className="px-3 py-2 border border-gray-200 rounded-lg text-gray-900"
                  />
                  <input
                    type="email"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    placeholder="Gmail cím"
                    className="px-3 py-2 border border-gray-200 rounded-lg text-gray-900"
                  />
                  <button
                    type="submit"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2 rounded-lg flex items-center justify-center gap-2"
                  >
                    <Plus size={18} /> Hozzáadás
                  </button>
                </div>
              </form>

              {/* Users list */}
              <div className="space-y-3">
                {allUsers.map(user => (
                  <div key={user.id} className={`rounded-xl border p-4 ${user.active ? 'bg-white border-gray-200' : 'bg-gray-100 border-gray-300 opacity-60'}`}>
                    {editingUser === user.id ? (
                      <div className="grid gap-3 md:grid-cols-5">
                        <input
                          type="text"
                          defaultValue={user.name}
                          id={`edit-name-${user.id}`}
                          className="px-3 py-2 border border-gray-200 rounded-lg text-gray-900"
                        />
                        <input
                          type="text"
                          defaultValue={user.class_name}
                          id={`edit-class-${user.id}`}
                          className="px-3 py-2 border border-gray-200 rounded-lg text-gray-900"
                        />
                        <input
                          type="email"
                          defaultValue={user.email}
                          id={`edit-email-${user.id}`}
                          className="px-3 py-2 border border-gray-200 rounded-lg text-gray-900"
                        />
                        <button
                          onClick={() => handleUpdateUser(user.id, {
                            name: document.getElementById(`edit-name-${user.id}`).value,
                            class_name: document.getElementById(`edit-class-${user.id}`).value,
                            email: document.getElementById(`edit-email-${user.id}`).value
                          })}
                          className="bg-emerald-600 text-white py-2 rounded-lg"
                        >
                          Mentés
                        </button>
                        <button onClick={() => setEditingUser(null)} className="text-gray-500">
                          Mégse
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                          <p className="font-semibold text-gray-800">{user.name}</p>
                          <p className="text-sm text-gray-500">{user.class_name} • {user.email}</p>
                          <p className="text-xs text-gray-400 font-mono">Kód: {user.code}</p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setEditingUser(user.id)}
                            className="px-3 py-1 text-sm text-emerald-600 hover:bg-emerald-50 rounded-lg"
                          >
                            Szerkesztés
                          </button>
                          <button
                            onClick={() => handleRegenerateCode(user.id)}
                            className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded-lg flex items-center gap-1"
                          >
                            <RefreshCw size={14} /> Új kód
                          </button>
                          <button
                            onClick={() => handleUpdateUser(user.id, { active: !user.active })}
                            className={`px-3 py-1 text-sm rounded-lg ${user.active ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}`}
                          >
                            {user.active ? 'Letiltás' : 'Aktiválás'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reasons management */}
          {adminTab === 'reasons' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-gray-800">Indokok kezelése</h2>
              
              {/* Add reason form */}
              <form onSubmit={handleAddReason} className="flex gap-3">
                <input
                  type="text"
                  value={newReasonName}
                  onChange={(e) => setNewReasonName(e.target.value)}
                  placeholder="Új indok neve"
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-xl"
                  required
                />
                <button
                  type="submit"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-2 rounded-xl flex items-center gap-2"
                >
                  <Plus size={18} /> Hozzáadás
                </button>
              </form>

              {/* Reasons list */}
              <div className="space-y-2">
                {allReasons.map(reason => (
                  <div key={reason.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-xl px-4 py-3">
                    <span className="text-gray-800">{reason.name}</span>
                    <button
                      onClick={() => handleDeleteReason(reason.id)}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* All requests */}
          {adminTab === 'all' && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-gray-800">Összes kérelem</h2>
              
              <div className="space-y-3">
                {allRequests.map(req => {
                  const status = getStatusColor(req.status);
                  return (
                    <div key={req.id} className={`${status.bg} border ${status.border} rounded-xl p-4`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`${status.text} text-sm font-semibold`}>{status.label}</span>
                        <span className="text-sm text-gray-500">{req.type}</span>
                      </div>
                      <p className="font-semibold text-gray-800">{req.user_name}</p>
                      <p className="text-sm text-gray-600">{req.class_name} • {req.date}</p>
                      <p className="text-xs text-gray-400 mt-1">Beküldve: {formatDate(req.created_at)}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // USER INTERFACE
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50">
      {/* Modern user header */}
      <div className="bg-gradient-to-r from-emerald-700 to-emerald-800 text-white py-4 px-4 shadow-xl">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Logo" className="w-10 h-10 rounded-xl" />
            <div>
              <h1 className="text-lg font-bold">Igazoláskezelő</h1>
              <p className="text-emerald-200 text-xs">{currentUser?.name}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-3 py-2 rounded-xl text-sm transition-colors">
            <LogOut size={16} /> Kilépés
          </button>
        </div>
      </div>

      {/* User tabs */}
      <div className="bg-white border-b border-emerald-100 shadow-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto flex">
          {[
            { id: 'new', label: 'Új kérelem', icon: Send },
            { id: 'list', label: 'Beküldött', icon: List },
            { id: 'profile', label: 'Adatok', icon: User },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 py-4 font-semibold transition-all border-b-3 ${
                activeTab === tab.id 
                  ? 'bg-emerald-50 text-emerald-700 border-b-2 border-emerald-600' 
                  : 'text-slate-500 hover:bg-slate-50 border-b-2 border-transparent'
              }`}
            >
              <tab.icon size={18} />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* New request form */}
        {activeTab === 'new' && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-800">Új igazolás / kikérő</h2>
            
            {submitSuccess && (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl p-4 text-center">
                Sikeresen beküldve! A kérelem feldolgozásra vár.
              </div>
            )}

            <form onSubmit={handleSubmitRequest} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">Típus</label>
                <select
                  value={formType}
                  onChange={(e) => { setFormType(e.target.value); setFormDate(''); }}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="Igazolás">Igazolás</option>
                  <option value="Kikérő">Kikérő</option>
                  <option value="E-igazolás">E-igazolás</option>
                  <option value="E-kikérő">E-kikérő</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">Név</label>
                <input
                  type="text"
                  value={currentUser?.name || ''}
                  disabled
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">Osztály / Tagozat</label>
                <input
                  type="text"
                  value={formClass}
                  onChange={(e) => setFormClass(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 focus:ring-2 focus:ring-emerald-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">
                  Dátum {(formType === 'Igazolás' || formType === 'E-igazolás') ? '(mai vagy korábbi)' : '(holnaptól)'}
                </label>
                <input
                  type="date"
                  value={formDate}
                  onChange={(e) => setFormDate(e.target.value)}
                  {...getDateConstraints()}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 focus:ring-2 focus:ring-emerald-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">Oka</label>
                <select
                  value={formReason}
                  onChange={(e) => setFormReason(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 focus:ring-2 focus:ring-emerald-500"
                  required
                >
                  <option value="">Válassz...</option>
                  {reasons.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>

              <button
                type="submit"
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                <Send size={20} /> Beküldés
              </button>
            </form>
          </div>
        )}

        {/* My requests list */}
        {activeTab === 'list' && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-gray-800">Beküldött kérelmeim</h2>
            
            {myRequests.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Még nincs beküldött kérelmed</p>
            ) : (
              <div className="space-y-3">
                {myRequests.map(req => {
                  const status = getStatusColor(req.status);
                  const isExpanded = expandedRequest === req.id;
                  
                  return (
                    <div
                      key={req.id}
                      className={`${status.bg} border ${status.border} rounded-xl overflow-hidden`}
                    >
                      <div
                        onClick={() => setExpandedRequest(isExpanded ? null : req.id)}
                        className="p-4 cursor-pointer"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className={`${status.text} text-sm font-bold`}>{status.label}</span>
                          {isExpanded ? <ChevronUp size={20} className="text-gray-400" /> : <ChevronDown size={20} className="text-gray-400" />}
                        </div>
                        <p className="text-gray-700"><span className="text-gray-500">Típus:</span> {req.type}</p>
                        <p className="text-gray-700"><span className="text-gray-500">Név:</span> {req.user_name}</p>
                        <p className="text-gray-700"><span className="text-gray-500">Osztály / Tagozat:</span> {req.class_name}</p>
                        <p className="text-gray-700"><span className="text-gray-500">Dátum:</span> {req.date}</p>
                        <p className="text-gray-500 text-sm mt-3">Beküldés dátuma: {formatDate(req.created_at)}</p>
                      </div>
                      
                      {isExpanded && (
                        <div className="px-4 pb-4 border-t border-gray-200 pt-3">
                          <p className="text-gray-700 mb-3"><span className="text-gray-500">Indok:</span> {req.reason}</p>
                          
                          {req.status === 'approved' && req.pdf_path && (
                            <a
                              href={`${API}/hianyzas/requests/${req.id}/pdf?token=${userToken}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
                            >
                              <Download size={16} /> PDF letöltése
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Profile data */}
        {activeTab === 'profile' && currentUser && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-800">Adataim</h2>
            
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-6 space-y-4">
              <div>
                <p className="text-sm text-gray-500">Név</p>
                <p className="text-lg font-medium text-gray-800">{currentUser.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Osztály / Tagozat</p>
                <p className="text-lg font-medium text-gray-800">{currentUser.class_name || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Gmail cím</p>
                <p className="text-lg font-medium text-gray-800">{currentUser.email || '-'}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
